import { createApi, fetchBaseQuery, retry } from "@reduxjs/toolkit/query/react";
import Axios from "axios";
import { config } from "../../../config";
import { IAssociate } from "../../../interfaces";
import { LocalStorageKeys } from "../../../utils";

export interface AccessPayload {
  access?: {
    grant?: {
      owners?: Array<string>;
      members?: Array<string>;
      notes?: string;
    };
    revoke?: {
      user_ids: Array<string>;
    };
  };
  visibility: Visibility;
}

export type Visibility = "public" | "private";

export const commonAPIs = createApi({
  reducerPath: "commonAPIs",
  baseQuery: retry(
    fetchBaseQuery({
      baseUrl: config.api_url,
      prepareHeaders: (headers) => {
        const token = localStorage.getItem(LocalStorageKeys.authToken);

        // If we have a token set in state, let's assume that we should be passing it.
        if (token) {
          headers.set("authorization", `Bearer ${token}`);
          headers.set("Content-Type", "application/json");
        }

        return headers;
      },
    }),
    { maxRetries: 2 }
  ),
  tagTypes: ["Common Attachment"],
  endpoints: (builder) => ({
    // Link Entity API
    linkModule: builder.mutation({
      query: ({ object_type, object_id, payload }) => ({
        url: `link/${object_type}/${object_id}`,
        method: "PUT",
        body: payload,
      }),
    }),

    // Un Link Entity API
    unlinkModule: builder.mutation({
      query: ({ object_type, object_id, payload }) => ({
        url: `unlink/${object_type}/${object_id}`,
        method: "PUT",
        body: payload,
      }),
    }),

    // Link And Unlink API
    linkOrUnlinkEntity: builder.mutation({
      query: (payload: IAssociate) => ({
        url: `associate`,
        method: "POST",
        body: payload,
      }),
    }),

    // Get hierarchy for a specific module
    getHierarchy: builder.query({
      query: ({ object_type, object_id }) => ({
        url: `children/${object_type}/${object_id}`,
        method: "GET",
      }),
    }),

    // User Access Management for Product, Project and Proposal
    setVisibility: builder.mutation({
      query: ({
        object_type,
        object_id,
        payload,
      }: {
        object_type: string;
        object_id: string;
        payload: AccessPayload;
      }) => ({
        url: `visibility/${object_type}/${object_id}`,
        method: "POST",
        body: payload,
      }),
    }),

    // Processing Status API's

    // Get processing status for list view and bom report
    getProcessingStatus: builder.query({
      query: ({ id, type }) => ({
        url: `/dm/processing_status/${type}/${id}`,
        method: "GET",
      }),
    }),

    // End of Processing Status API's

    // Supplier Search
    searchSupplier: builder.mutation({
      query: (payload) => ({
        url: "https://xqji6arbdty5kab3aobef6bhzq0kecpa.lambda-url.us-east-1.on.aws/",
        method: "POST",
        body: payload,
      }),
    }),

    // Get Dependency API
    getDependencies: builder.query({
      query: ({
        object_type,
        object_id,
      }: {
        object_type: string;
        object_id: string;
      }) => ({
        url: `dependencies/${object_type}/${object_id}`,
        method: "GET",
      }),
    }),

    // GET Shared Document Info API
    getSharedDocumentInfo: builder.mutation({
      async queryFn(_arg, _queryApi, _extraOptions) {
        const { token } = _arg;
        let result: any = {};
        try {
          const resData = await Axios({
            url: `${config.api_url}external/v1/document-share/info`,
            method: "GET",
            headers: {
              authorization: `Bearer ${token}`,
              "X-Device-Id": btoa(navigator.userAgent),
              "Content-Type": "application/json",
            },
          });
          result = { data: resData?.data };
        } catch (err: any) {
          result = { error: err?.response?.data };
        } finally {
          return result;
        }
      },
    }),

    // Generate OTP API
    generateOTPForAccess: builder.mutation({
      async queryFn(_arg, _queryApi, _extraOptions) {
        const { token } = _arg;
        let result: any = {};
        try {
          const resData = await Axios({
            url: `${config.api_url}external/v1/document-share/grant`,
            method: "POST",
            headers: {
              authorization: `Bearer ${token}`,
              "X-Device-Id": btoa(navigator.userAgent),
              "Content-Type": "application/json",
            },
          });
          result = { data: resData };
        } catch (err: any) {
          result = { error: err?.response?.data };
        } finally {
          return result;
        }
      },
    }),

    // Validate OTP and Get Document API
    validateOTPForDocumentAccess: builder.mutation({
      async queryFn(_arg, _queryApi, _extraOptions) {
        const { otp, token } = _arg;
        let result: any = {};
        try {
          const resData = await Axios({
            url: `${config.api_url}external/v1/document-share/access?otp=${otp}`,
            method: "GET",
            headers: {
              authorization: `Bearer ${token}`,
              "X-Device-Id": btoa(navigator.userAgent),
              "Content-Type": "application/json",
            },
          });
          result = { data: resData?.data };
        } catch (err: any) {
          result = { error: err?.response?.data };
        } finally {
          return result;
        }
      },
    }),
  }),
});

export const {
  useLinkModuleMutation,
  useUnlinkModuleMutation,
  useLazyGetHierarchyQuery,
  useSetVisibilityMutation,
  useGetProcessingStatusQuery,
  useSearchSupplierMutation,
  useGetDependenciesQuery,
  useLazyGetDependenciesQuery,
  useGetSharedDocumentInfoMutation,
  useGenerateOTPForAccessMutation,
  useValidateOTPForDocumentAccessMutation,
  useLinkOrUnlinkEntityMutation
} = commonAPIs;
